import BaseDocumentService from '@/services/BaseDocumentService';
import ContractAsideSignatureClient from '@/components/contract/ContractAsideSignatureClient.vue';
import ModalOtp from '@/components/modal/ModalOtp.vue';
import { DOCUMENT_TYPE } from '@/typings/enums/DOCUMENT_TYPE';
import { SIGNATURE_STATUS } from '@/typings/enums/SIGNATURE_STATUS';
import { USER_TYPE } from '@/typings/enums/USER_TYPE';
import { getApi } from '@/utils/ApiTool';
import { askTempWorkerInfos, confirmPassword } from '@/utils/tools';
import { Bus, itemHas, useAside, useLoader, useModal, useNotif } from '@orion.ui/orion';
import { isArray } from 'lodash-es';
import { nextTick } from 'vue';
import { CONTRACT_STATUS } from '@/typings/enums/CONTRACT_STATUS';
import useBusService from '../BusService';


type ContractStatus = {
	label: string,
	labelColor: Orion.Color
}

export class ContractService extends BaseDocumentService<IClientContractItemForAgencyModel> {
	private documentType: Armado.ContractDocumentType;

	protected get api () {
		if (this.documentType === DOCUMENT_TYPE.ClientContract) {
			return getApi(this.self.userType, this.documentType);
		} else {
			return getApi(this.self.userType, this.documentType);
		}
	}

	constructor (documentType: Armado.ContractDocumentType) {
		super();
		this.documentType = documentType;
	}


	private itemsAreContracts (items: any[]): items is Armado.ContractListItem[] {
		return typeof items[0] === 'object';
	}

	async signAsync (items: {id: number}[], filterItems = true, documentType?: DOCUMENT_TYPE) {
		if (documentType) this.documentType = documentType as Armado.ContractDocumentType;

		if (filterItems && this.itemsAreContracts(items)) {
			items = items.filter(x =>
				!!x.document && !x.document?.integratedDate
        && (x.document.signatureInfoStatus ^ SIGNATURE_STATUS.AgencySigned) === SIGNATURE_STATUS.None,
			);
		}

		if (!items || items.length === 0) return;

		if (this.self.isTempWorker()) {
			if (items.length > 20) {
				useNotif.warning(`Attention`, `Vous ne pouvez pas signer plus de 20 contrats simultanément`);
			}

			return new Promise(async (resolve) => {
				try {
					if (this.documentType === DOCUMENT_TYPE.TempWorkerContract) {
						useLoader().show('Vos documents sont en cours de préparation pour la signature');
						const { data: procedure } = await getApi(USER_TYPE.TempWorker, this.documentType).getSiriusProcedure(items.map(x => x.id));

						useModal({
							Nested: ModalOtp,
							NestedProps: {
								procedure,
								ids: items.mapKey('id'),
							},
							title: 'Signature',
							size: 'lg',
							customClass: 'modal--signature',
							events: {
								'signed': (Modal, docs: {id: number, status: boolean}[]) => {
									docs.forEach(x => useBusService().emit(`temp-worker-contract-${x.id}:signed`, x.status));
									Bus.emit(`DashboardNewDocOrSignCardSetupService:reload`);
									setTimeout(() => Modal.close(), 5000);
									resolve(true);
								},
								'leave-start': () => {
									resolve(false);
								},
							},
						});
					}
				} catch (error: any) {
					if (isArray(error.response?.data)) {
						if (await askTempWorkerInfos(error.response.data[0])) {
							nextTick(async () => {
								await this.signAsync(items, filterItems, documentType);
							});
						}
					} else {
						useNotif.warning(`Oops`, error.response?.data);
					}
					resolve(false);
				} finally {
					useLoader().hide();
				}
			});
		} else if (this.self.isClient()) {
			if (items.length > 40) {
				useNotif.warning(`Attention`, `Vous ne pouvez pas signer plus de 40 contrats simultanément`);
			}
			if (await confirmPassword()) {
				return new Promise((resolve) => {
					useAside({
						Nested: ContractAsideSignatureClient,
						NestedProps: {
							documentIds: items.map(x => x.id),
							documentType: this.documentType,
						},
						events:
							{
								leaveStart: () => resolve(false),
								'sign': async (Aside, docs: {id: number, status: boolean}[]) => {
									try {
										if (itemHas(this.api, 'sign')) {
											await this.api.sign(docs.mapKey('id'));
											docs.forEach(x => useBusService().emit(`client-contract-${x.id}:signed`, x.status));
											Bus.emit(`DashboardNewDocOrSignCardSetupService:reload`);
											setTimeout(() => Aside.close(), 1000);
										}
										resolve(true);
									} catch (error: any) {
										useNotif.warning(`Oops`, error.response?.data);
										resolve(false);
									} finally {
										Aside.close();
									}
								},
							},
					});
				});
			}
		}
	}

	async validateAsync (contractId: number) {
		if (itemHas(this.api, 'validate')) {
			try {
				await this.api.validate(contractId);
				useBusService().emit(`client-contract-${contractId}-list-item:validated`, true);
				useBusService().emit(`client-contract-${contractId}-details:validated`, true);
				useNotif.success('Le contrat a bien été validé.');
			} catch (error: any) {
				useNotif.danger(error.message);
			}
		}
	}

	async cancelValidationAsync (contractId: number) {
		if (itemHas(this.api, 'cancelValidation')) {
			try {
				await this.api.cancelValidation(contractId);
				useBusService().emit(`client-contract-${contractId}-list-item:validated`, false);
				useBusService().emit(`client-contract-${contractId}-details:validated`, false);
				useNotif.success('La validation a bien été annulée.');
			} catch (error: any) {
				useNotif.danger(error.message);
			}
		}
	}

	async getProofAsync (id: number) {
		this.api.getProof(id);
	}

	getContractStatus (params:{
		status: CONTRACT_STATUS,
		signatureInProcess?: boolean,
		isSigned?: boolean,
		validationDate?: string,
		isPaperSigned?: boolean}): ContractStatus {
		if (params.status === CONTRACT_STATUS.Canceled) {
			return {
				label: 'Contrat annulé',
				labelColor: 'danger',
			};
		} else if (params.signatureInProcess) {
			return {
				label: 'Contrat signé',
				labelColor: 'success',
			};
		} else if (!params.isSigned) {
			return {
				label: params.validationDate ? 'Validé' : 'Contrat non signé',
				labelColor: params.validationDate ? 'info' : 'warning',
			};
		}
		return {
			label: params.isPaperSigned ? `Contrat retourné à l'agence` : 'Contrat signé',
			labelColor: 'success',
		};
	}
}


export default function useContractService (documentType: Armado.ContractDocumentType) {
	return new ContractService(documentType);
}
